/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
// libraries
import React, { useEffect } from 'react';
import {
    object, shape, string, func,
} from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import { Query } from '@apollo/client/react/components';
import mbpLogger from 'mbp-logger';
import QueryString from 'qs';
import { compose } from 'recompose';
import Media from 'react-media';
import { bindActionCreators } from 'redux';
// components
import PLASkeleton from '../GraphqlSkeletonComponents/PLASkeleton';
import PDPSkeleton from '../GraphqlSkeletonComponents/PDPSkeleton';
import MobilePDPSkeleton from '../GraphqlSkeletonComponents/MobileSkeleton/MobilePDPSkeleton';
import GraphqlProductPageBuilder from './GraphqlProductPageBuilder';
import RedirectWithStatus from '../GraphqlCommonComponents/RedirectWithStatus/RedirectWithStatus';
// redux, hooks, helpers
import { GRAPHQL_ENV } from '../../../gql';
import findProductPageByPartNumber from '../../../gql/queries/findProductPageByPartNumber';
import isProductListingAd from '../../../helpers/isProductListingAd';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { coreBrowsingPageViewSuccessTest } from '../../../helpers/tracking/common/commonTrackingHelpers';
import withPageView from '../../../helpers/tracking/hocs/withPageView';
import ProductNotAvailableContainer from './Partials/ProductNotAvailable/ProductNotAvailableContainer';
import ProductPageContainerWithPassport from './GraphqlProductPageContainerWithPassport';
import { setPassportBundleData } from '../../../../state/ducks/Passport/Passport-Action';
import CrossMerchContainer from '../CrossMerchContainer/CrossMerchContainer';
import { getBrand, getContactId } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getActiveABTests } from '../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import { skuMapsTo } from '../../../helpers/vaseConfigurations';
import { getIsBot, getSSRSearch, getCustomerData } from '../../../../state/ducks/App/App-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import PDPSeoSchemaOutOfStock from './Partials/GraphqlProductSeoSchema/PDPSeoSchemaOutOfStock';

const GraphqlProductPageContainer = ({
    location, brand, page, page: { code }, featureFlags, presentation_family, setBundleData, activeABTests, key, customerData,
}) => {
    const urlParams = QueryString.parse(location.search);
    const baseProductPartNumber = QueryString.parse(location.search.replace(/^\?/, ''));
    const isPlaEnabled = featureFlags['is-pla-flow-enabled'];
    const isSmallSkuSelectedPdpEnabled = featureFlags['is-smallest-sku-selected-pdp-ab-test-enabled'];
    const dispatch = useDispatch();

    const isVaseConfiguratorEnabled = featureFlags['is-vase-configurator-enabled'] && activeABTests['which-vase-configurator'] === 'VARIANT';
    const isPmall = (code.indexOf('1030-P') !== -1); // TODO: We can do better...

    let partNumber = code;
    // TODO: bladerunner: do something better to identify vase config
    if (isVaseConfiguratorEnabled && skuMapsTo(code)) {
        partNumber = skuMapsTo(code);
    }

    /**
     * passing null because we need to use the variables from gql "variable" prop, we need dynamic price as third param to include the pricing
     * option in query arguments.
     */
    const PRODUCT_PAGE_QUERY = findProductPageByPartNumber(null, null, true);

    const { data: smallestSkuData, loading: smallSkuSelectedLoading,  variables: smallestSkuVariable } = useSalesforcePersonalizedContentQuery({
        queryName: 'smallSkuSelectedPdp',
        interactionName: `${brand?.domain} - Get Campaign - Smallest Sku Selected PDP`,
        featureFlag: isSmallSkuSelectedPdpEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const smallestSkuSelectedGroup = smallestSkuData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const smallestSkuSelectedEnabled = smallestSkuSelectedGroup?.toLowerCase?.() === 'test';
    const isPassportAfterCtaABTestEnabled = featureFlags['is-pdp-pla-passport-after-cta-ab-test-enabled'];
    const { loading: passportAfterCtaLoading, variables: passportAfterCtaVariable, data: passportAfterCtaData } = useSalesforcePersonalizedContentQuery({
        queryName: 'passportAfterCta',
        interactionName: `${brand?.domain} - Get Campaign - PDP Passport Under CTA`,
        featureFlag: isPassportAfterCtaABTestEnabled,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });
    const isPassportAfterCtaData = passportAfterCtaData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const isPassportAfterCta = isPassportAfterCtaData?.toLowerCase() === 'test';
    useClickStreamCustomEventsTracking({
        salesforceResponse: passportAfterCtaData?.contentSF,
        isFireImpression: true,
        page: { type: 'pdp' },
    });
    useClickStreamCustomEventsTracking({
        salesforceResponse: smallestSkuData?.contentSF,
        isFireImpression: true,
        page: { type: 'pdp' },
    });
    useEffect(() => {
        if (typeof passportAfterCtaData !== 'undefined' && typeof window !== 'undefined' && !window.wuPDPPassportAfterCtaEvent && isPassportAfterCtaData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'PDP Passport Under CTA',
                eventLabel: isPassportAfterCtaData?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_detail: isPassportAfterCtaData?.toLowerCase() === 'test' ? 'Variant' : 'Control',
                feature_element: 'PDP Passport Under CTA',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuPDPPassportAfterCtaEvent = true;
        }
        return () => {
            window.wuPDPPassportAfterCtaEvent = false;
        };
    }, [isPassportAfterCtaData]);
    useEffect(() => {
        if (typeof smallestSkuData !== 'undefined' && typeof window !== 'undefined' && !window.wuSmallestSkuEventFired && smallestSkuData) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'PDP - Default SKU Selection',
                eventLabel: smallestSkuSelectedEnabled ? 'Treatment' : 'Control',
                feature_detail: smallestSkuSelectedEnabled ? 'Treatment' : 'Control',
                feature_element: 'PDP - Default SKU Selection',
                feature_category: 'Experiment',
            };
            dispatch(trackEvent(eventParam));
            window.wuSmallestSkuEventFired = true;
        }
        return () => {
            window.wuSmallestSkuEventFired = false;
        };
    }, [smallestSkuData]);

    return (
        <Query
            fetchPolicy="cache-and-network"
            query={PRODUCT_PAGE_QUERY}
            variables={
                {
                    brand: isPmall ? 'personalizationmall' : brand.domain,
                    partNumber,
                    pricingOptions: null,
                }
            }
        >
            {({
                loading, error, data, refetch, variables,
            }) => {
                if (loading || smallSkuSelectedLoading || smallestSkuVariable?.skip || passportAfterCtaVariable?.skip || passportAfterCtaLoading) {
                    if (featureFlags['is-pla-flow-enabled'] && isProductListingAd(location.search)) {
                        return <PLASkeleton />;
                    }
                    return (
                        <Media
                            query="(min-width: 599px)"
                        >
                            {(matches) => (matches
                                ? <PDPSkeleton />
                                : <MobilePDPSkeleton />

                            )}
                        </Media>
                    );
                }
                if (error) {
                    mbpLogger.logError({
                        appName: process.env.npm_package_name,
                        query: PRODUCT_PAGE_QUERY,
                        component: 'GraphqlProductPageContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }
                if (!data) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: PRODUCT_PAGE_QUERY,
                        component: 'GraphqlProductPageContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                const {
                    product: productData, defaultContent, content, isPassportBundleItem,
                } = data.findProductPageByPartNumber;
                const product = JSON.parse(JSON.stringify(productData));
                const seoData = content?.entries?.[0]?.seo ? content.entries[0].seo : {};
                const yotpoCustomerReviewsGallery = content?.entries?.[0]?.customer_reviews_gallery ? content.entries[0].customer_reviews_gallery : {};

                if (!data?.findProductPageByPartNumber) {
                    return (
                        <>
                            <PDPSeoSchemaOutOfStock
                                brand={brand}
                                partNumber={content?.entries?.[0]?.product_base_code}
                                name={content?.entries?.[0]?.name}
                                location={location}
                            />
                            <ProductNotAvailableContainer brand={brand} productUnavailableName={null} code={code} seoData={seoData} />
                        </>
                    );
                }
                // if no product data or product data doesnt exsist render best sellers
                if (!product || !product.availability || !product.productSkus?.length > 0) {
                    const productUnavailableName = content?.entries?.[0]?.name || null;

                    if (featureFlags['is-product-unavailable-404']) {
                        // client side - will it suffice for local development / uat ?
                        // occasionally if a product is cached in a category/collection/content page there will be a brief time the product will show but be unavailable
                        if (typeof window !== 'undefined' && window.location.host) {
                            return (
                                <>
                                    <PDPSeoSchemaOutOfStock
                                        brand={brand}
                                        partNumber={content?.entries?.[0]?.product_base_code}
                                        name={content?.entries?.[0]?.name}
                                        location={location}
                                    />
                                    <ProductNotAvailableContainer brand={brand} productUnavailableName={productUnavailableName} code={code} seoData={seoData} />
                                </>
                            );
                        }
                        // We want to serve a 404 as per Merkle(SEO) for when a products not available
                        return (
                            <>
                                <PDPSeoSchemaOutOfStock
                                    brand={brand}
                                    partNumber={content?.entries?.[0]?.product_base_code}
                                    name={content?.entries?.[0]?.name}
                                    location={location}
                                />
                                <RedirectWithStatus to={location.pathname} status="404" productUnavailableName={productUnavailableName} />
                            </>
                        );
                    }
                    return (
                        <>
                            <PDPSeoSchemaOutOfStock
                                brand={brand}
                                partNumber={content?.entries?.[0]?.product_base_code}
                                name={content?.entries?.[0]?.name}
                                location={location}
                            />
                            <ProductNotAvailableContainer brand={brand} productUnavailableName={productUnavailableName} code={code} seoData={seoData} />
                        </>
                    );
                }

                // url param for isPassportBundleProduct
                const regex = new RegExp(/[?&]pb=true/i);

                const passportBundle = content?.entries?.[0]?.passport_bundle || {};
                let bundlePromotionValid = true;
                if (passportBundle?.promotion_date_range?.to_date) {
                    const now = Date.now();
                    const toDate = new Date(passportBundle.promotion_date_range.to_date);
                    if (now > toDate) bundlePromotionValid = false;
                }
                if (passportBundle?.promotion_date_range?.from_date) {
                    const now = Date.now();
                    const fromDate = new Date(passportBundle.promotion_date_range.from_date);
                    if (now < fromDate) bundlePromotionValid = false;
                }

                if (location?.search
                    && regex.test(location.search)
                    && passportBundle?.check_to_enable_passport_bundle
                    && bundlePromotionValid
                    && passportBundle?.passportsku?.length) {
                    return (
                        // TODO Refactor this, way too much duplicate code - integrate into standard layout
                        <ProductPageContainerWithPassport
                            content={defaultContent}
                            productBaseContent={content}
                            seoData={seoData}
                            product={product}
                            urlParams={urlParams}
                            page={page}
                            isPlaEnabled={isPlaEnabled}
                            isPla={isProductListingAd(location.search)}
                            presentation_family={presentation_family}
                            isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                            baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                            isProductListingAd={isProductListingAd}
                            brand={brand}
                            variables={variables}
                            code={code}
                            refetch={refetch}
                            customerData={customerData}
                            smallestSkuSelectedEnabled={smallestSkuSelectedEnabled}
                            isPassportAfterCta={isPassportAfterCta}
                        />
                    );
                }

                setBundleData({
                    enabled: false,
                });

                // Render Standard PDP Layout
                return (
                    <div data-pagetype="product">
                        <CrossMerchContainer
                            productBrandId={product.brandId}
                            crossMerchBrand={product.brand}
                            refetchQuery={() => brand.domain === variables.brand && refetch({ brand: product.brand, partNumber: code })}
                        >
                            <GraphqlProductPageBuilder
                                // TODO: this naming needs to be reconciled as its really confusing
                                baseProductPartNumber={baseProductPartNumber?.baseProductPartNumber || null}
                                content={defaultContent}
                                isPassportBundleItem={isPassportBundleItem || false} // If this value comes back as undefined in the PDP call default to false
                                isPla={isProductListingAd(location.search)}
                                page={page}
                                presentation_family={presentation_family}
                                product={product}
                                productBaseContent={content}
                                seoData={seoData}
                                urlParams={urlParams}
                                key={key}
                                yotpoCustomerReviewsGallery={yotpoCustomerReviewsGallery}
                                customerData={customerData}
                                smallestSkuSelectedEnabled={smallestSkuSelectedEnabled}
                                isPassportAfterCta={isPassportAfterCta}
                            />
                        </CrossMerchContainer>
                    </div>
                );
            }}
        </Query>
    );
};

GraphqlProductPageContainer.propTypes = {
    brand: object.isRequired,
    page: shape({
        code: string,
    }).isRequired,
    location: object.isRequired,
    featureFlags: object.isRequired,
    presentation_family: string.isRequired,
    customerData: object.isRequired,
    setBundleData: func.isRequired,
    activeABTests: object.isRequired,
    key: string.isRequired,
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
    activeABTests: getActiveABTests(state),
    presentation_family: getPresentationFamily(state),
    contactId: getContactId(state),
    isBot: getIsBot(state),
    customerData: getCustomerData(state),
    isScrapperPathVariable: getSSRSearch(state),
});

const mapDispatchToProps = (dispatch) => ({
    setBundleData: bindActionCreators(setPassportBundleData, dispatch),
});

const enhance = compose(
    withPageView({
        pageViewSuccessTest: coreBrowsingPageViewSuccessTest,
        updateEveryRender: true,
    }),
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlProductPageContainer);
